import * as React from 'react';
import { useParams } from 'react-router-dom';

import { PageBackLink } from '@/member/page-back-link';

import { PER_EVENT_LOCKED } from '../event-registrations/constants';
import { PerEventShell } from '../event-registrations/per-event-page';
import { EveryoneAttending, MeetOnlyToggle } from '../matching/everyone-attending-panel';

import { useAttendeeRoster, useMyMeetList } from './api';

/**
 * `MEM-011` AC-4 — the full roster: **every visible attendee in one scrollable list, with the
 * private-attendee row at the end.**
 *
 * ## Drawn from Brad's updated design, 2026-09-25 (TW 44189308)
 *
 * "See all" under Who's coming opens a page that starts with **the event's header card** — the
 * date tile, the name, the date and place, *View your ticket*, Requests, *You're going*, and the
 * Before · During · After stepper — over **Everyone attending**: the People I want to meet toggle,
 * the attendee search, the two-column cards with Save to Meet and Connect, and the private row
 * spanning both columns at the end. Both halves are the ones the event's Matches page already
 * draws (`PerEventShell` and `EveryoneAttending`), shared rather than re-transcribed; the Matches
 * page's own *Your matches* block is left out.
 *
 * This reverses design note #140, which kept a plain "Who's going" list with no search. The search
 * is safe here for the reason it is safe on Matches: since Sumit's ruling of 2026-09-11 only a
 * member who has reserved can read the roster at all, so it is a list of the room they are going
 * to, not a directory. A member who has not reserved never sees "See all"; opening the address
 * directly gets the shell's lock card, with a way to reserve.
 *
 * Still one list: no pagination and no sort. The search and the meet-list toggle narrow what is
 * shown; clearing them shows everyone again.
 *
 * ## Gated by `ProtectedRoute`, not by this component
 *
 * `MEM-019` AC-2 puts the roster behind a session, and the route carries the gate (`App.tsx`), so
 * a signed-out visitor gets the Sign-in required interstitial rather than an empty list.
 */
export function AttendeeRosterPage(): React.ReactElement {
  const { eventId = '' } = useParams<{ eventId: string }>();

  return (
    <PerEventShell
      eventId={eventId}
      surface="attendees"
      locked={PER_EVENT_LOCKED.attendees}
      back={
        <PageBackLink to={`/app/events/${encodeURIComponent(eventId)}`}>
          <span>Back to event</span>
        </PageBackLink>
      }
    >
      {() => <RosterBody eventId={eventId} />}
    </PerEventShell>
  );
}

function RosterBody({ eventId }: { readonly eventId: string }): React.ReactElement {
  const rosterQuery = useAttendeeRoster(eventId, { enabled: true });
  const meetQuery = useMyMeetList(eventId, { enabled: true });
  const [meetOnly, setMeetOnly] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const meetList = meetQuery.data?.meetList ?? null;
  const savedRefs = React.useMemo(
    () => new Set((meetList?.entries ?? []).map((entry) => entry.attendeeRef)),
    [meetList],
  );
  const roster = rosterQuery.data?.roster;
  // No toggle over a closed roster: there is nobody on it to narrow to.
  const open = roster !== undefined && roster.closed === null;

  return (
    <EveryoneAttending
      eventId={eventId}
      roster={roster}
      pending={rosterQuery.isPending}
      savedRefs={savedRefs}
      savedTotal={savedRefs.size}
      meetOnly={meetOnly}
      query={query}
      onQuery={setQuery}
      onBrowseEveryone={() => {
        setMeetOnly(false);
        setQuery('');
      }}
      headingLevel="h1"
      toggle={
        open ? (
          <MeetOnlyToggle
            count={savedRefs.size}
            on={meetOnly}
            onToggle={() => setMeetOnly(!meetOnly)}
          />
        ) : null
      }
    />
  );
}
